import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import type { User, Group } from '@/types';
import { Check, Plus, Trash2, ArrowLeft, Users, Shield, User as UserIcon, Mail, Trash, Eye, Building, Activity, Calendar, Globe, Database } from 'lucide-react';
import { useUsers } from '@/store/user';
import { useGroups } from '@/store/group';
import { useAssignments, type RoleAssignment } from '@/store/assignment';
import { useRoles } from '@/store/role';
import { useTenants } from '@/store/tenant';
import { useResources } from '@/store/resource';


import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { TabBar } from '@/components/ui/TabBar';
import { InfoGrid } from '@/components/ui/InfoGrid';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';

export const ManageUserPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { setBreadcrumbs } = useLayout();

    const allGroups = useGroups((state) => state.groups);
    const listAllGroups = useGroups((state) => state.list);
    const listAllRoles = useRoles((state) => state.list);
    const allRoles = useRoles((state) => state.roles);
    const listPermissions = useRoles((state) => state.listPermissions);
    const { tenants, list: listTenants } = useTenants();
    const { resources, listResources } = useResources();

    const tabs = [
        { id: 'details', label: 'Identity Details', icon: UserIcon },
        { id: 'groups', label: 'Group Memberships', icon: Users },
        { id: 'roles', label: 'Assigned Roles', icon: Shield },
    ];
    const queryParams = new URLSearchParams(location.search);
    const [activeTab, setActiveTab] = useState<string>(queryParams.get('tab') || 'details');

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tab = queryParams.get('tab');
        if (tab && ['details', 'groups', 'roles'].includes(tab)) {
            setActiveTab(tab);
        }
    }, [location.search]);

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
        navigate(`?tab=${tabId}`, { replace: true });
    };

    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [userGroups, setUserGroups] = useState<Group[]>([]);
    const [rolePermissions, setRolePermissions] = useState<Record<string, string[]>>({});

    // Role assignments list
    const [userAssignments, setUserAssignments] = useState<RoleAssignment[]>([]);

    // modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState<'groups' | 'roles' | null>(null);
    const [modalValue, setModalValue] = useState<string>(''); // Group ID or Role ID
    const [scopeType, setScopeType] = useState<'platform' | 'tenant' | 'custom'>('tenant');
    const [customScope, setCustomScope] = useState<string>('');
    const [isSavingModal, setIsSavingModal] = useState(false);

    // tenant user status approval
    const [tenantUserStatus, setTenantUserStatus] = useState<number | null>(null);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

    // permissions preview state
    const [selectedRolePermissions, setSelectedRolePermissions] = useState<string[]>([]);
    const [loadingPermissions, setLoadingPermissions] = useState(false);

    // Confirm-revoke modal state
    const [confirmRevoke, setConfirmRevoke] = useState<RoleAssignment | null>(null);
    const [revokePermissions, setRevokePermissions] = useState<string[]>([]);
    const [loadingRevokePerms, setLoadingRevokePerms] = useState(false);

    // Confirm-delete modal state
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        Promise.all([
            useUsers.getState().get(id),
            listAllGroups(),
            listAllRoles(),
            listTenants(),
            useUsers.getState().listGroups(id),
            listResources()
        ]).then(async ([u, , , , groups]) => {
            setUser({
                ...u,
                is_active: u.is_active ?? !u.deleted_at
            } as any);
            setUserGroups(groups);
            
            // Use status directly from the user object (returned by mgmt/v1/users/{id})
            if (u.status !== undefined && u.status !== null) {
                setTenantUserStatus(Number(u.status));
            }
            setBreadcrumbs([
                { name: 'Users', to: '/users' },
                { name: u.name || 'Manage User' }
            ]);

            // Load assignments using user's identity_id if present
            const targetPrincipalId = u.identity_id || u.id || id;
            try {
                const assigns = await useAssignments.getState().listForPrincipal(targetPrincipalId, 'user');
                setUserAssignments(assigns);
            } catch (err) {
                console.error(err);
                toast.error('Failed to load user assignments');
            }
        })
            .catch((e) => {
                console.error(e);
                toast.error('Failed to load user data');
            })
            .finally(() => setLoading(false));
    }, [id, listAllGroups, listAllRoles, listTenants, setBreadcrumbs, listResources]);

    useEffect(() => {
        if (userAssignments.length === 0) return;
        const uniqueRoleIds = Array.from(new Set(userAssignments.map(a => a.role_id)));
        const missingRoleIds = uniqueRoleIds.filter(roleId => !rolePermissions[roleId]);
        if (missingRoleIds.length === 0) return;

        const permPromises = missingRoleIds.map(roleId =>
            listPermissions(roleId)
                .then(perms => ({ roleId, perms }))
                .catch(() => ({ roleId, perms: [] }))
        );
        Promise.all(permPromises).then(results => {
            setRolePermissions(prev => {
                const updated = { ...prev };
                results.forEach(r => {
                    updated[r.roleId] = r.perms;
                });
                return updated;
            });
        });
    }, [userAssignments, listPermissions, rolePermissions]);


    // Load permissions preview when selected role changes (assign modal)
    useEffect(() => {
        if (modalType === 'roles' && modalValue) {
            setLoadingPermissions(true);
            listPermissions(modalValue)
                .then(setSelectedRolePermissions)
                .catch(() => setSelectedRolePermissions([]))
                .finally(() => setLoadingPermissions(false));
        } else {
            setSelectedRolePermissions([]);
        }
    }, [modalType, modalValue, listPermissions]);

    // Load permissions lost when confirm-revoke modal opens
    useEffect(() => {
        if (confirmRevoke) {
            setLoadingRevokePerms(true);
            listPermissions(confirmRevoke.role_id)
                .then(setRevokePermissions)
                .catch(() => setRevokePermissions([]))
                .finally(() => setLoadingRevokePerms(false));
        } else {
            setRevokePermissions([]);
        }
    }, [confirmRevoke, listPermissions]);

    const submitDetails = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !id) return;
        setIsSaving(true);
        try {
            await useUsers.getState().update(id, { 
                name: user.name,
                tenant_id: user.tenant_id || null,
                status: tenantUserStatus !== null ? tenantUserStatus : undefined,
                valid_from: user.valid_from ? new Date(user.valid_from).toISOString() : undefined,
                valid_till: user.valid_till ? new Date(user.valid_till).toISOString() : undefined,
            } as any);
        } catch {
            // Handled by store
        } finally {
            setIsSaving(false);
        }
    };

    const formatDateForInput = (dateString?: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    };

    const addGroup = async (groupId: string) => {
        if (!id) return;
        setIsSavingModal(true);
        try {
            await useGroups.getState().addMember(groupId, id);
            const updated = await useUsers.getState().listGroups(id);
            setUserGroups(updated);
            toast.success('Group assigned');
            setModalOpen(false);
        } catch {
            toast.error('Failed to assign group');
        } finally {
            setIsSavingModal(false);
        }
    };

    const removeGroup = async (groupId: string) => {
        if (!id) return;
        try {
            await useGroups.getState().removeMember(groupId, id);
            setUserGroups(prev => prev.filter(g => g.id !== groupId));
            toast.success('Membership removed');
        } catch {
            toast.error('Failed to remove membership');
        }
    };

    const addRoleAssignment = async () => {
        if (!id || !user || !modalValue) return;
        setIsSavingModal(true);

        // Resolve scope path
        let targetScope = '/platform';
        if (scopeType === 'tenant') {
            targetScope = `/tenants/${user.tenant_id}`;
        } else if (scopeType === 'custom') {
            targetScope = customScope;
        }

        try {
            await useAssignments.getState().create({
                role_id: modalValue,
                principal_id: id,
                principal_type: 'user',
                scope_id: targetScope
            });
            const updated = await useAssignments.getState().listForPrincipal(id, 'user');
            setUserAssignments(updated);
            toast.success('Role assigned successfully');
            setModalOpen(false);
        } catch (e: any) {
            toast.error(e.message || 'Failed to assign role');
        } finally {
            setIsSavingModal(false);
        }
    };

    const removeRoleAssignment = async (assignmentId: string) => {
        if (!id) return;
        try {
            await useAssignments.getState().delete(assignmentId, undefined, id);
            setUserAssignments(prev => prev.filter(a => a.id !== assignmentId));
            toast.success('Role assignment revoked');
            setConfirmRevoke(null);
        } catch (e: any) {
            toast.error(e.message || 'Failed to revoke role assignment');
        }
    };

    const handleDeleteUser = async () => {
        if (!id) return;
        try {
            await useUsers.getState().delete(id);
            navigate('/m/users');
        } catch {
            // Handled by store
        }
    };

    if (loading || !user) return <div className="h-full flex items-center justify-center"><Loader /></div>;

    const nonUserGroups = allGroups.filter(g => !userGroups.some(ug => ug.id === g.id));

    return (
        <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title={user.name || 'Manage User'}
                subtitle={user.email}
                actions={
                    <Button variant="ghost" onClick={() => navigate('/m/users')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                }
            />

            <div className="mt-8">
                <TabBar
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={handleTabChange}
                    className="mb-8"
                />

                <div className="grid gap-6">
                    {activeTab === 'details' && (
                        <div className="max-w-4xl animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Core Identity"
                                subtitle="Basic account information and contact identifiers."
                                className="glass"
                            >
                                <form onSubmit={submitDetails} className="space-y-8">
                                    <InfoGrid cols={2}>
                                        <InputField
                                            label="Full Name"
                                            value={user.name || ''}
                                            onChange={(e) => setUser({ ...user, name: e.target.value })}
                                            required
                                            icon={<UserIcon className="w-4 h-4" />}
                                            helpText="The primary display name for this user."
                                        />
                                        <InputField
                                            label="Email (Immutable)"
                                            value={user.email}
                                            disabled
                                            icon={<Mail className="w-4 h-4" />}
                                            helpText="Email addresses are managed via Identity Providers."
                                        />

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                Tenant
                                            </label>
                                            <div className="relative">
                                                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                                                    <Building className="w-4 h-4" />
                                                </div>
                                                <select
                                                    className="w-full px-4 py-2 pl-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm transition-all duration-200 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 dark:text-white appearance-none"
                                                    value={user.tenant_id || ''}
                                                    onChange={(e) => setUser({ ...user, tenant_id: e.target.value })}
                                                >
                                                    <option value="">Unassigned</option>
                                                    {tenants.map(t => (
                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Assign user to a specific tenant.</p>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                Account Status
                                            </label>
                                            <div className="relative">
                                                {/* Dropdown Trigger */}
                                                <button
                                                    type="button"
                                                    className="w-full flex items-center justify-between px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm transition-all duration-200 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 dark:text-white font-bold"
                                                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Activity className="w-4 h-4 text-slate-400" />
                                                        <span className={`w-2.5 h-2.5 rounded-full border border-white dark:border-slate-950 shrink-0 ${
                                                            Number(tenantUserStatus) === 0 ? 'bg-yellow-500' :
                                                            Number(tenantUserStatus) === 1 ? 'bg-green-500' :
                                                            Number(tenantUserStatus) === 2 ? 'bg-slate-400' :
                                                            Number(tenantUserStatus) === 3 ? 'bg-red-500' : 'bg-transparent'
                                                        }`} />
                                                        <span>
                                                            {Number(tenantUserStatus) === 0 && 'Pending'}
                                                            {Number(tenantUserStatus) === 1 && 'Active'}
                                                            {Number(tenantUserStatus) === 2 && 'Inactive'}
                                                            {Number(tenantUserStatus) === 3 && 'Suspended'}
                                                        </span>
                                                    </div>
                                                    <span className="text-slate-400 text-xs">▼</span>
                                                </button>

                                                {/* Dropdown Options */}
                                                {statusDropdownOpen && (
                                                    <>
                                                        <div 
                                                            className="fixed inset-0 z-10" 
                                                            onClick={() => setStatusDropdownOpen(false)}
                                                        />
                                                        <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-20 py-1 overflow-hidden">
                                                            {[
                                                                { value: 0, label: 'Pending', color: 'bg-yellow-500' },
                                                                { value: 1, label: 'Active', color: 'bg-green-500' },
                                                                { value: 2, label: 'Inactive', color: 'bg-slate-400' },
                                                                { value: 3, label: 'Suspended', color: 'bg-red-500' },
                                                            ].map((s) => {
                                                                // Hide Pending if it's not the current status
                                                                if (s.value === 0 && Number(tenantUserStatus) !== 0) return null;
                                                                return (
                                                                    <button
                                                                        key={s.value}
                                                                        type="button"
                                                                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 text-left text-sm font-semibold dark:text-white"
                                                                        onClick={() => {
                                                                            setTenantUserStatus(s.value);
                                                                            setUser({ ...user, is_active: s.value === 1 });
                                                                            setStatusDropdownOpen(false);
                                                                        }}
                                                                        disabled={s.value === 0}
                                                                    >
                                                                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${s.color}`} />
                                                                        <span>{s.label}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                {Number(tenantUserStatus) === 0 && "Tenant Access: Pending approval. User cannot access apps."}
                                                {Number(tenantUserStatus) === 1 && "Tenant Access: Approved. User can access apps."}
                                                {Number(tenantUserStatus) === 2 && "Tenant Access: Inactive. User cannot access apps."}
                                                {Number(tenantUserStatus) === 3 && "Tenant Access: Suspended. User cannot access apps."}
                                            </p>
                                        </div>

                                        <InputField
                                            label="Valid From (Optional)"
                                            type="datetime-local"
                                            value={formatDateForInput(user.valid_from)}
                                            onChange={(e) => setUser({ ...user, valid_from: e.target.value })}
                                            icon={<Calendar className="w-4 h-4" />}
                                            helpText="When this account should become active."
                                        />

                                        <InputField
                                            label="Valid Till (Optional)"
                                            type="datetime-local"
                                            value={formatDateForInput(user.valid_till)}
                                            onChange={(e) => setUser({ ...user, valid_till: e.target.value })}
                                            icon={<Calendar className="w-4 h-4" />}
                                            helpText="When this account should expire."
                                        />
                                    </InfoGrid>

                                    <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                                        <Button
                                            type="submit"
                                            isLoading={isSaving}
                                            className="min-w-40 gap-2"
                                        >
                                            <Check className="w-4 h-4" />
                                            Update Profile
                                        </Button>
                                    </div>
                                </form>
                            </Card>



                            <Card className="mt-6 border-red-100 dark:border-red-900/20 bg-red-50/10" title="Danger Zone">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-bold text-slate-900 dark:text-white">Delete User Account</h4>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Permanently delete this user account and all associated data.</p>
                                    </div>
                                    <Button variant="danger" size="sm" onClick={() => { setConfirmDeleteOpen(true); setDeleteConfirmText(""); }} className="gap-2">
                                        <Trash className="w-4 h-4" />
                                        Delete Account
                                    </Button>
                                </div>
                            </Card>
                        </div>
                    )}

                    {activeTab === 'groups' && (
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Group Assignments"
                                subtitle="Groups provide inherited roles and access permissions."
                                noPadding
                                className="glass"
                                actions={
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="gap-2 font-bold text-orange-500"
                                        onClick={() => {
                                            setModalType('groups');
                                            setModalValue('');
                                            setModalOpen(true);
                                        }}
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Membership
                                    </Button>
                                }
                            >
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                                                <th className="px-6 py-3">Group Name</th>
                                                <th className="px-6 py-3">System Key</th>
                                                <th className="px-6 py-3 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                            {userGroups.map((g) => (
                                                <tr key={g.id} className="hover:bg-orange-50/50 dark:hover:bg-orange-500/5 transition-colors group">
                                                    <td className="px-6 py-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-xs text-slate-500">
                                                                {g.name?.charAt(0)}
                                                            </div>
                                                            <span className="text-sm font-bold text-slate-900 dark:text-white">{g.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        <span className="text-xs font-mono text-slate-500 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded-md">grp_{g.name?.toLowerCase().replace(/\s+/g, '_')}</span>
                                                    </td>
                                                    <td className="px-6 py-3 text-right">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeGroup(g.id)}
                                                            className="text-slate-400 hover:text-red-500 p-2"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {userGroups.length === 0 && (
                                                <tr>
                                                    <td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic text-sm">
                                                        This user is not a member of any groups.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        </div>
                    )}

                    {activeTab === 'roles' && (
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Role Assignments"
                                subtitle="Roles assigned to this user, grouped by tenant and scope."
                                className="glass"
                                actions={
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="gap-2 font-bold text-orange-500"
                                        onClick={() => {
                                            setModalType('roles');
                                            setModalValue('');
                                            setScopeType('tenant');
                                            setCustomScope('');
                                            setModalOpen(true);
                                        }}
                                    >
                                        <Plus className="w-4 h-4" />
                                        Assign Role
                                    </Button>
                                }
                            >
                                {(() => {
                                    if (userAssignments.length === 0) {
                                        return (
                                            <div className="py-12 text-center text-slate-400 italic text-sm">
                                                No roles assigned.
                                            </div>
                                        );
                                    }

                                    // Helper to get Tenant ID from scope_id or user.tenant_id
                                    const getTenantIdForAssign = (scopeId: string) => {
                                        if (scopeId.includes('platform/tenants/')) {
                                            const parts = scopeId.split('/');
                                            const idx = parts.indexOf('tenants');
                                            if (idx !== -1 && parts[idx + 1]) {
                                                return parts[idx + 1];
                                            }
                                        }
                                        return user.tenant_id || 'platform';
                                    };

                                    // Group assignments by tenant, then scope
                                    const grouped: Record<string, Record<string, RoleAssignment[]>> = {};
                                    userAssignments.forEach(a => {
                                        const tId = getTenantIdForAssign(a.scope_id);
                                        if (!grouped[tId]) grouped[tId] = {};
                                        if (!grouped[tId][a.scope_id]) grouped[tId][a.scope_id] = [];
                                        grouped[tId][a.scope_id].push(a);
                                    });

                                    return (
                                        <div className="space-y-8">
                                            {Object.entries(grouped).map(([tenantId, scopesGroup]) => {
                                                const tenantObj = tenants.find(t => t.id === tenantId);
                                                return (
                                                    <div key={tenantId} className="space-y-4">
                                                        {/* Tenant Group Header */}
                                                        <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-slate-800">
                                                            {tenantObj?.logo_url ? (
                                                                <img src={tenantObj.logo_url} alt="" className="w-6 h-6 rounded object-contain bg-slate-50 dark:bg-slate-800 p-0.5" />
                                                            ) : (
                                                                <Building className="w-4 h-4 text-slate-400" />
                                                            )}
                                                            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">{tenantObj?.name || 'Platform'} Scope</h3>
                                                        </div>

                                                        {/* Scopes Group */}
                                                        <div className="space-y-4 pl-4 border-l-2 border-slate-100 dark:border-slate-800">
                                                            {Object.entries(scopesGroup).map(([scopeId, assignsList]) => {
                                                                const res = resources.find(r => r.uri === scopeId);
                                                                const isRoot = scopeId.startsWith('platform/tenants/') && !scopeId.includes('/applications/');
                                                                let scopeName = scopeId;
                                                                let scopeLogo = undefined;
                                                                let isApp = false;

                                                                if (isRoot) {
                                                                    scopeName = 'root (Tenant Scope)';
                                                                    scopeLogo = tenantObj?.logo_url || res?.metadata?.tenant_logo || undefined;
                                                                } else if (res?.metadata?.application_name) {
                                                                    scopeName = `App: ${res.metadata.application_name}`;
                                                                    scopeLogo = res.metadata.application_logo || undefined;
                                                                    isApp = true;
                                                                }

                                                                return (
                                                                    <div key={scopeId} className="bg-slate-50/30 dark:bg-slate-900/20 rounded-2xl p-4 border border-slate-100 dark:border-slate-800/80">
                                                                        {/* Scope Header */}
                                                                        <div className="flex items-center gap-2 mb-3">
                                                                            {scopeLogo ? (
                                                                                <img src={scopeLogo} alt="" className="w-5 h-5 rounded object-contain bg-white dark:bg-slate-950 p-0.5" />
                                                                            ) : isApp ? (
                                                                                <Globe className="w-4 h-4 text-purple-400" />
                                                                            ) : (
                                                                                <Database className="w-4 h-4 text-slate-400" />
                                                                            )}
                                                                            <div>
                                                                                <div className="text-xs font-bold text-slate-900 dark:text-white">{scopeName}</div>
                                                                                <div className="text-[10px] font-mono text-slate-400">{scopeId}</div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Roles */}
                                                                        <div className="space-y-2.5">
                                                                            {assignsList.map(a => {
                                                                                const matchedRole = allRoles.find(r => r.id === a.role_id);
                                                                                const perms = rolePermissions[a.role_id] || [];
                                                                                return (
                                                                                    <div key={a.id} className="flex items-start justify-between p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200/40 dark:border-slate-800/40">
                                                                                        <div className="space-y-1.5">
                                                                                            <div className="flex items-center gap-1.5">
                                                                                                <Shield className="w-3.5 h-3.5 text-orange-500" />
                                                                                                <span className="text-xs font-bold font-mono text-slate-800 dark:text-slate-200">{matchedRole?.name || a.role_id}</span>
                                                                                            </div>
                                                                                            <div className="flex flex-wrap gap-1">
                                                                                                {perms.length > 0 ? (
                                                                                                    perms.map(p => (
                                                                                                        <span key={p} className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[9px] font-mono text-slate-500 dark:text-slate-450">
                                                                                                            {p}
                                                                                                        </span>
                                                                                                    ))
                                                                                                ) : (
                                                                                                    <span className="text-[9px] text-slate-400 italic">No permissions associated</span>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            onClick={() => setConfirmRevoke(a)}
                                                                                            className="text-slate-400 hover:text-red-500 p-1.5 shrink-0"
                                                                                        >
                                                                                            <Trash2 className="w-4 h-4" />
                                                                                        </Button>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </Card>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for adding associations */}
            {modalOpen && modalType && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <Card
                        className="w-full max-w-lg shadow-2xl overflow-hidden glass"
                        title={modalType === 'groups' ? 'Assign Group' : 'Assign Role'}
                    >
                        <div className="space-y-6">
                            {modalType === 'groups' ? (
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Target Group</label>
                                    <select
                                        className="w-full bg-slate-50/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition-all font-bold text-slate-900 dark:text-white"
                                        value={modalValue}
                                        onChange={(e) => setModalValue(e.target.value)}
                                    >
                                        <option value="" disabled>Choose a group...</option>
                                        {nonUserGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                    </select>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Select Role</label>
                                        <select
                                            className="w-full bg-slate-50/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition-all font-bold text-slate-900 dark:text-white"
                                            value={modalValue}
                                            onChange={(e) => setModalValue(e.target.value)}
                                        >
                                            <option value="" disabled>Choose a role...</option>
                                            {allRoles.map(r => <option key={r.id} value={r.id}>{r.name} ({r.id})</option>)}
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Scope Type</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setScopeType('tenant')}
                                                className={`px-3 py-2 border rounded-xl text-xs font-bold transition-all ${scopeType === 'tenant' ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' : 'bg-slate-50/50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 hover:border-slate-300'}`}
                                            >
                                                Tenant Scope
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setScopeType('platform')}
                                                className={`px-3 py-2 border rounded-xl text-xs font-bold transition-all ${scopeType === 'platform' ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' : 'bg-slate-50/50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 hover:border-slate-300'}`}
                                            >
                                                Platform Scope
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setScopeType('custom')}
                                                className={`px-3 py-2 border rounded-xl text-xs font-bold transition-all ${scopeType === 'custom' ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' : 'bg-slate-50/50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 hover:border-slate-300'}`}
                                            >
                                                Custom Scope
                                            </button>
                                        </div>
                                    </div>

                                    {scopeType === 'tenant' && (
                                        <div className="p-3 bg-slate-50/50 dark:bg-slate-900/50 rounded-xl border border-slate-200/50 dark:border-slate-800/50 text-[11px] font-mono text-slate-500">
                                            Target: /tenants/{user.tenant_id}
                                        </div>
                                    )}

                                    {scopeType === 'platform' && (
                                        <div className="p-3 bg-slate-50/50 dark:bg-slate-900/50 rounded-xl border border-slate-200/50 dark:border-slate-800/50 text-[11px] font-mono text-slate-500">
                                            Target: /platform
                                        </div>
                                    )}

                                    {scopeType === 'custom' && (
                                        <InputField
                                            label="Custom Scope Path"
                                            placeholder="e.g. /tenants/{id}/apps/{app_id}"
                                            value={customScope}
                                            onChange={(e) => setCustomScope(e.target.value)}
                                            icon={<Shield className="w-4 h-4" />}
                                        />
                                    )}

                                    {/* Permissions Preview Section */}
                                    {modalValue && (
                                        <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                                            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                                <Eye className="w-3.5 h-3.5" />
                                                Granted Permissions Preview
                                            </h4>
                                            {loadingPermissions ? (
                                                <div className="py-4 text-center text-xs text-slate-400"><Loader /></div>
                                            ) : selectedRolePermissions.length === 0 ? (
                                                <div className="text-xs text-slate-400 italic py-2">No permissions associated with this role.</div>
                                            ) : (
                                                <div className="max-h-32 overflow-y-auto border border-slate-100 dark:border-slate-800/50 rounded-xl p-3 bg-slate-50/30 dark:bg-slate-950/20 divide-y divide-slate-100/50 dark:divide-slate-900/50">
                                                    {selectedRolePermissions.map(p => (
                                                        <div key={p} className="py-1.5 text-xs font-mono text-slate-600 dark:text-slate-400 first:pt-0 last:pb-0">{p}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                                <Button variant="ghost" className="flex-1" onClick={() => setModalOpen(false)}>Cancel</Button>
                                <Button
                                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                                    disabled={!modalValue || isSavingModal || (scopeType === 'custom' && !customScope)}
                                    isLoading={isSavingModal}
                                    onClick={() => modalType === 'groups' ? addGroup(modalValue) : addRoleAssignment()}
                                >
                                    Assign
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
            {/* Confirm-revoke modal */}
            {confirmRevoke && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-950 border border-red-200 dark:border-red-900/40 p-6 rounded-2xl w-full max-w-md shadow-2xl">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center shrink-0">
                                <Trash2 className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-slate-900 dark:text-white">Revoke Role Assignment</h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                    Removing <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{confirmRevoke.role_id}</span> from this user at scope <span className="font-mono text-xs">{confirmRevoke.scope_id}</span>.
                                </p>
                            </div>
                        </div>

                        <div className="mb-5">
                            <h4 className="text-[11px] font-bold text-red-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                <Eye className="w-3.5 h-3.5" />
                                Permissions That Will Be Lost
                            </h4>
                            {loadingRevokePerms ? (
                                <div className="py-4 flex justify-center"><Loader /></div>
                            ) : revokePermissions.length === 0 ? (
                                <p className="text-xs text-slate-400 italic py-2">No permissions listed for this role.</p>
                            ) : (
                                <div className="max-h-36 overflow-y-auto border border-red-100 dark:border-red-900/30 rounded-xl p-3 bg-red-50/30 dark:bg-red-950/20 divide-y divide-red-100/50 dark:divide-red-900/20">
                                    {revokePermissions.map(p => (
                                        <div key={p} className="py-1.5 text-xs font-mono text-red-600 dark:text-red-400 first:pt-0 last:pb-0">{p}</div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <Button variant="ghost" className="flex-1" onClick={() => setConfirmRevoke(null)}>Cancel</Button>
                            <Button
                                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                                onClick={() => removeRoleAssignment(confirmRevoke.id)}
                            >
                                Revoke Access
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {confirmDeleteOpen && user && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-950 border border-red-200 dark:border-red-900/40 p-6 rounded-2xl w-full max-w-md shadow-2xl">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center shrink-0">
                                <Trash className="w-5 h-5 text-red-500" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-base font-bold text-slate-900 dark:text-white">Delete User Account</h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                    Are you sure you want to permanently delete user <span className="font-bold text-slate-700 dark:text-slate-300">{user.name || user.email}</span>? This action cannot be undone.
                                </p>
                                <div className="mt-4">
                                    <p className="text-xs text-red-650 dark:text-red-400 font-medium mb-1.5">
                                        Type <span className="font-bold">"{user.email}"</span> to confirm
                                    </p>
                                    <input
                                        type="text"
                                        value={deleteConfirmText}
                                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                                        className="w-full px-3 py-1.5 text-sm border border-red-200 dark:border-red-800 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500/50"
                                        placeholder={user.email}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <Button
                                variant="ghost"
                                className="flex-1"
                                onClick={() => {
                                    setConfirmDeleteOpen(false);
                                    setDeleteConfirmText("");
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                                disabled={deleteConfirmText !== user.email}
                                onClick={async () => {
                                    await handleDeleteUser();
                                    setConfirmDeleteOpen(false);
                                    setDeleteConfirmText("");
                                }}
                            >
                                Delete Account
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
